import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { listQuerySchema } from './pagination';

/*
 * Reports — ROADMAP.md §10.4, data-model.md §10.
 *
 * **Four types, matching the prototype**: Summary, Employee, Client, Jobs.
 *
 * ---
 *
 * ## Money in is two columns, never one
 *
 * **This section used to say there was no revenue figure anywhere**, because
 * nothing knew what a job had been charged until invoices existed. Phase 12
 * built them and the owner asked for revenue on 2026-08-06, after the
 * full-walkthrough audit filed the question.
 *
 * The old reasoning still decides the shape, so it is kept rather than deleted.
 * Multiplying today's rate card by last quarter's jobs would produce a number
 * that looks exactly like revenue, would be handed to an accountant, and would
 * not reconcile against the bank — defect P1's shape, a figure carrying the
 * confidence of a fact. **So neither new column is an estimate.**
 *
 *   - **Invoiced** — the total of invoices *issued* in the window, by
 *     `issue_date`. Drafts never count; a voided invoice was un-billed.
 *   - **Payments received** — money that actually arrived in the window, by
 *     `payment.paid_on`, refunds excluded.
 *
 * **They are not netted together.** They answer different questions and
 * reconcile against different things — the invoices, and the bank — and they
 * sit on different date columns, so a January invoice paid in March appears in
 * January's Invoiced and March's Payments received. Their difference is what is
 * still owed, which is the Invoices screen's job to chase.
 *
 * Expenses remain money out, recorded by hand with a receipt (10.1).
 */

export const REPORT_TYPES = ['summary', 'employee', 'client', 'jobs'] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  summary: 'Summary',
  employee: 'Employee',
  client: 'Client',
  jobs: 'Jobs',
};

export const REPORT_TYPE_DESCRIPTIONS: Record<ReportType, string> = {
  summary: 'The business over the period — jobs by status, hours worked, money in and money out.',
  employee: 'One row per cleaner: jobs worked, hours, overtime, and expenses submitted.',
  client:
    'One row per customer: jobs booked and completed, hours on site, what they were invoiced and what they paid.',
  jobs: 'One row per job, with its client, service, status and hours.',
};

export const REPORT_FORMATS = ['csv', 'pdf'] as const;
export type ReportFormat = (typeof REPORT_FORMATS)[number];

/**
 * The presets ROADMAP.md §10.4 names, plus a custom range.
 *
 * Resolved on the **server**, against the business's own time zone. "Today" from
 * a browser in another zone is a different day, and Cedar City is UTC-6 in
 * summer — from about 6pm local a browser computing its own dates is already on
 * tomorrow.
 */
export const REPORT_PRESETS = ['today', 'last_7', 'last_30', 'last_year', 'custom'] as const;
export type ReportPreset = (typeof REPORT_PRESETS)[number];

export const REPORT_PRESET_LABELS: Record<ReportPreset, string> = {
  today: 'Today',
  last_7: 'Last 7 days',
  last_30: 'Last 30 days',
  last_year: 'Last year',
  custom: 'Custom range',
};

export const reportQuerySchema = z
  .object({
    type: z.enum(REPORT_TYPES),
    preset: z.enum(REPORT_PRESETS).default('last_30'),
    from: calendarDateSchema.optional(),
    to: calendarDateSchema.optional(),
    /** Narrow an Employee or Client report to one of them. */
    employeeId: z.uuid().optional(),
    clientId: z.uuid().optional(),
  })
  .refine((value) => value.preset !== 'custom' || (value.from && value.to), {
    message: 'A custom range needs a start and an end',
    path: ['from'],
  });

export type ReportQuery = z.infer<typeof reportQuerySchema>;

export const reportDownloadSchema = reportQuerySchema.safeExtend({
  format: z.enum(REPORT_FORMATS),
});

export type ReportDownloadQuery = z.infer<typeof reportDownloadSchema>;

// ---------------------------------------------------------------------------
// The shape every report comes back in
// ---------------------------------------------------------------------------

/**
 * How a column is rendered, so CSV and PDF do not each decide for themselves.
 *
 * **`money` is integer cents and `minutes` is whole minutes** — the value on the
 * wire is always the integer, and the formatting happens at the display edge in
 * each renderer. A report that shipped `"$1,250.00"` as a string would be one
 * nobody could total in a spreadsheet, which is most of what a CSV is for.
 */
export type ReportColumnKind = 'text' | 'number' | 'money' | 'minutes' | 'date';

export interface ReportColumn {
  key: string;
  header: string;
  kind: ReportColumnKind;
}

export type ReportCell = string | number | null;

export interface ReportTable {
  type: ReportType;
  title: string;
  /** The window it covers, as a sentence — it goes in the file. */
  subtitle: string;
  columns: ReportColumn[];
  rows: ReportCell[][];

  /**
   * A closing line of totals, or null where a total means nothing.
   *
   * The Jobs report has no total row on purpose: summing a column of statuses is
   * meaningless, and the one number worth having — how many jobs — is the row
   * count, which every renderer already shows.
   */
  totals: ReportCell[] | null;

  /**
   * A kind per **row**, overriding the column's — for a table whose rows are
   * different measures rather than the same measure repeated.
   *
   * **Only the Summary report uses this, and it exists because the screen was
   * wrong.** That report is a list of label/value pairs — "Jobs booked 2",
   * "Hours clocked 4560", "Expenses approved 13316" — so its value column
   * cannot have one kind: those three are a count, whole minutes and integer
   * cents. Typed `text`, they rendered as the stored integers, and **`4560`
   * where `76h 00m` belongs is exactly the class of thing this project treats as
   * a defect** rather than a rough edge. Found by looking at the screen.
   *
   * `undefined` means the column's own kind applies, which is every other
   * report.
   */
  rowKinds?: (ReportColumnKind | undefined)[];

  from: string;
  to: string;
  generatedAt: string;
}

/**
 * The four cards above the reports screen — **defect P1, and this type is the
 * fix**.
 *
 * The prototype's `/admin/reports` shows `—` in all four permanently: a network
 * trace on page load fires `auth/me` and `notifications` and nothing else, so
 * the cards are decoration. **These four are its four**, by name, so the screen
 * can be compared against it directly.
 *
 * **Every field is a number and never null.** There is no shape this type can
 * take that renders a dash — `KpiCard` needs `value={null}` for that, and none
 * of these can be null. A business with no jobs this month has `0` jobs this
 * month, which is a fact rather than an absence.
 *
 * Jobs and completions are **over the selected period**; employees and clients
 * are **current counts**, because "active clients last March" is a different and
 * much less useful question than "how many do we have".
 */
export interface ReportKpis {
  totalJobs: number;
  completedJobs: number;
  activeEmployees: number;
  activeClients: number;
  /** Repeated from the report so the cards can label their own window. */
  from: string;
  to: string;
}

// ---------------------------------------------------------------------------
// The history
// ---------------------------------------------------------------------------

export const REPORT_RUN_STATUSES = ['queued', 'ok', 'error'] as const;
export type ReportRunStatus = (typeof REPORT_RUN_STATUSES)[number];

export const REPORT_RUN_STATUS_LABELS: Record<ReportRunStatus, string> = {
  queued: 'Queued',
  ok: 'Generated',
  error: 'Failed',
};

/**
 * One row of the history.
 *
 * **No file, and no link to one.** `report_run` stores the parameters that
 * produced a report rather than the report itself — decided 2026-08-06, and the
 * reasoning is in migration 023's header. `rowCount` is what makes the history
 * useful without the bytes: a re-run producing a different count is the signal
 * that something underneath changed.
 */
export interface ReportRunView {
  id: string;
  reportType: ReportType;
  reportTypeLabel: string;
  format: ReportFormat;

  status: ReportRunStatus;
  statusLabel: string;
  errorMessage: string | null;

  rowCount: number | null;

  /** Enough to re-run it, and shown as a sentence. */
  from: string;
  to: string;
  rangeLabel: string;

  requestedByName: string;
  createdAt: string;
}

export const reportRunListQuerySchema = listQuerySchema.extend({
  type: z.enum([...REPORT_TYPES, 'all']).default('all'),
});

export type ReportRunListQuery = z.infer<typeof reportRunListQuerySchema>;
